import { DestinationAudioComponent, ReverbAudioComponent } from "audio_output";
import { AudioContextManager, SoundLoader } from "audio_utils";
import { Application, Container, Sprite, Ticker } from "pixi.js";
import { DynamicType, DynamicVolumeMap } from "score_data";
import { SFDriverConfig, SFScoreDriver } from "score_performance";
import { BitmapMusicRenderer, PixiAppHelper } from "score_render";
import { SoundLibManager, SoundLibType } from "sound_lib";
import { BrowserUtils } from "ts_utils";
import { AppSettings } from "../settings/AppSettings";
import { AppContainer } from "../ui/AppContainer";
import { pixiAppHelper, setMainPixiApp } from "../ui/pixi_apps";
import { RaAssessorFactory } from "./RaAssessorFactory";
import { RAControls } from "./RAControls";
import { RAPlayer } from "./RAPlayer";

export class PlayerInitializer {
	private _playerUi:HTMLDivElement;
	private _container:Container;
	private _driver:SFScoreDriver;
	
	public get raControls() { return this._raControls; }
	private _raControls:RAControls;
	
	public get raPlayer() { return this._raPlayer; }
	private _raPlayer:RAPlayer;
	
	//should create SFLoopDriver and pass to both NotationPlayer and 
	//LoopSequencer
	constructor(){
		
	}
	
	public async loadApplication():Promise<void> {
		this._playerUi = AppContainer.get().playerUi;
		await this._createStage();
		await this._createDriver();
		await this._createPlayer();
	}
	
	private async _createStage():Promise<void> {
		let canvasDiv = this._playerUi.querySelector('#p-score-stageDiv');
		let canvas = this._playerUi.querySelector('#stageCanvas');
		
		//create the app and main container
		let app = new Application();
		await app.init({
			canvas: canvas as HTMLCanvasElement,
			backgroundColor: 0xFFFFFF,
			antialias: true,
			sharedTicker: false
		});
		
		Ticker.shared.stop();
		Ticker.system.stop();
		
		let pah = new PixiAppHelper(app);
		
		pah.renderOnRequest = true;
		pah.setAutoResizeElement(canvasDiv as HTMLDivElement);
		pah.enableAutoResize();
		
		this._container = new Container();
		app.stage.addChild(this._container);
		
		setMainPixiApp(pah);
	}
	
	private async _createDriver():Promise<void> {
		let r:string = AppSettings.resourcesPath;
		let isSafari = BrowserUtils.isSafari();
		let mapPath:string = isSafari
			? `${r}sounds/mslib_sfz/map_safari.json`
			: `${r}sounds/mslib_sfz/map.json`;
			
		AudioContextManager.initContext();
		let ctx = AudioContextManager.context;
		let destination = new DestinationAudioComponent(ctx);
		
		let reverbComponent = new ReverbAudioComponent(ctx);
		let reverbUrl = `${r}sounds/IMreverbs/Five_Columns_Long.wav`;
		let impulseBuffer = await SoundLoader.getAudioBufferFromUrl(reverbUrl,
			AudioContextManager.context);
		reverbComponent.setReverbImpulse(impulseBuffer);
		reverbComponent.connect(destination);
		
		DynamicType.volumeMap = new DynamicVolumeMap();
		DynamicType.volumeMap.PPPP = 0.05;
		DynamicType.volumeMap.PPP = 0.1;
		DynamicType.volumeMap.PP = 0.15;
		DynamicType.volumeMap.P = 0.25;
		DynamicType.volumeMap.MP = 0.35;
		DynamicType.volumeMap.MF = 0.45;
		DynamicType.volumeMap.F = 0.55;
		DynamicType.volumeMap.FF = 0.65;
		DynamicType.volumeMap.FFF = 0.75;
		DynamicType.volumeMap.FFFF = 0.85;
		
		BitmapMusicRenderer.maxRenderers = 1;
		
		let config = new SFDriverConfig(SoundLibType.SFZ, { 
			soundMapPath: mapPath,
			loadMapOnInit: false,
			loadPresetsOnInit: false });
		config.outputComponent = reverbComponent;
		
		this._driver = await SFScoreDriver.create(pixiAppHelper, ctx, config);
		SoundLibManager.get().dbName = "musicShareDb";
		await this._driver.loadSoundMap(mapPath);
		await this._driver.loadPresets(null);
	}
	
	private async _createPlayer():Promise<void> {
		this._raPlayer = new RAPlayer(this._driver, this._container);
		
		let assessFactory = new RaAssessorFactory();
		await assessFactory.loadAnimations();
		this._driver.assessorFactory = assessFactory;
		
		let controlsUi = this._playerUi.querySelector('#p-headScoreFoot') as
			HTMLDivElement;
		this._raControls = new RAControls(controlsUi, this._raPlayer);
	}
}